import type { MaybeRefOrGetter } from 'vue';
import type { GlobalCollectionId, Uuid } from '#layers/data-model/public';
import { computed, toValue } from 'vue';
import { isGeneratedKey, useStorageKey } from '#layers/base/public';
import { useBlockContext } from '#layers/block-engine/utils/block/api/context';
import { isBlockSerialized } from '#layers/block-engine/utils/block/editor';

export interface UseBlockStorageKeyOptions {
	/**
	 * Optional collection ID to include in the storage key.
	 */
	collectionId?: MaybeRefOrGetter<GlobalCollectionId | null>;
	/** User ID for personal state; omit for shared state. */
	userId?: MaybeRefOrGetter<Uuid | null>;
}

/**
 * Generates a reactive storage key scoped by block context, user, and collection details.
 *
 * @param {MaybeRefOrGetter<string>} key - The base key used for identifying the storage.
 * @param {UseBlockStorageKeyOptions} [options] - Optional configuration for scoping.
 * @return An object containing the computed storage key and individual key components.
 */
export function useBlockStorageKey(key: MaybeRefOrGetter<string>, options?: UseBlockStorageKeyOptions) {
	const { getChild, getRootBlock, getRootContext } = useBlockContext();

	const rootBlockId = computed(() => {
		const rootBlock = getRootBlock();

		if (isBlockSerialized(rootBlock) && !isGeneratedKey(rootBlock.id)) {
			return rootBlock.id;
		}

		return null;
	});

	const childBlockId = computed(() => getChild().id);
	const collectionId = computed(() => toValue(options?.collectionId) ?? getRootContext().collectionId ?? null);
	const { storageKey, ownerId } = useStorageKey(key, {
		scope: () => [rootBlockId.value, collectionId.value, childBlockId.value],
		ownerId: options?.userId,
	});

	return {
		storageKey,
		rootBlockId,
		childBlockId,
		collectionId,
		ownerId,
	};
}
